import {
    Box,
    FormControl,
    MenuItem,
    Select,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import React from 'react';

import type { IMvpRole } from './MvpSubNav';

interface IMvpRoleSwitcherProps {
    roles: IMvpRole[];
    activeRole?: string;
    onRoleChange?: (roleId: string) => void;
}

function MvpRoleSwitcher({ roles, activeRole, onRoleChange }: IMvpRoleSwitcherProps) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const handleToggleChange = (_event: React.MouseEvent<HTMLElement>, newRole: string | null) => {
        if (newRole && onRoleChange) {
            onRoleChange(newRole);
        }
    };

    const handleSelectChange = (event: any) => {
        if (onRoleChange) {
            onRoleChange(event.target.value);
        }
    };

    if (isMobile) {
        return (
            <FormControl size="small" sx={{ minWidth: 120 }}>
                <Select
                    value={activeRole || ''}
                    onChange={handleSelectChange}
                    displayEmpty
                    sx={{
                        fontSize: '0.75rem',
                        '& .MuiSelect-select': {
                            py: 0.5,
                            px: 1,
                        },
                    }}
                >
                    {roles.map((role) => (
                        <MenuItem key={role.id} value={role.id}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {role.icon}
                                <Typography variant="caption">{role.label}</Typography>
                            </Box>
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        );
    }

    return (
        <ToggleButtonGroup
            value={activeRole}
            exclusive
            onChange={handleToggleChange}
            size="small"
            sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: '4px',
                p: 0.5,
                '& .MuiToggleButton-root': {
                    px: 1.5,
                    py: 0.25,
                    textTransform: 'none',
                    border: 'none',
                    color: 'text.disabled',
                    borderRadius: 0,
                    minWidth: 'auto',
                    fontSize: '0.75rem',
                    '&:first-of-type': {
                        borderTopLeftRadius: 4,
                        borderBottomLeftRadius: 4,
                    },
                    '&:last-of-type': {
                        borderTopRightRadius: 4,
                        borderBottomRightRadius: 4,
                    },
                    '&.Mui-selected': {
                        color: 'text.primary',
                        bgcolor: 'action.hover',
                        fontWeight: 500,
                        '&:hover': {
                            bgcolor: 'action.hover',
                            color: 'text.primary',
                        },
                    },
                    '&:hover': {
                        bgcolor: 'action.hover',
                        color: 'text.secondary',
                    },
                },
            }}
        >
            {roles.map((role) => (
                <ToggleButton key={role.id} value={role.id}>
                    <Typography variant="caption" sx={{ fontWeight: 'inherit' }}>
                        {role.label}
                    </Typography>
                </ToggleButton>
            ))}
        </ToggleButtonGroup>
    );
}

export default MvpRoleSwitcher;
